"use client";

import { TrendingDownSolidIcon } from "@louez/ui/icons";
import { useCallback, useEffect, useMemo, useState } from "react";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";

import { format, setHours, setMinutes } from "date-fns";
import {
  AlertCircle,
  ArrowRight,
  CalendarIcon,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  ImageIcon,
  Layers,
  Play,
  Ruler,
  Users,
  BedDouble,
  Bath,
} from "lucide-react";
import { useTranslations } from "next-intl";

import type { BusinessHours, NauticalProductDetails } from "@louez/types";
import { Button } from "@louez/ui";
import { Dialog, DialogHeader, DialogPopup, DialogTitle } from "@louez/ui";
import { Popover, PopoverContent, PopoverTrigger } from "@louez/ui";
import { Calendar } from "@louez/ui";
import { ScrollArea } from "@louez/ui";
import { Badge } from "@louez/ui";
import { cn, formatCurrency, minutesToPriceDuration } from "@louez/utils";

import {
  generateTimeSlots,
  getAvailableTimeSlots,
  isDateAvailable,
} from "@/lib/utils/business-hours";
import { type PricingMode, getMinStartDate, isTimeSlotAvailable } from "@/lib/utils/duration";
import {
  getDefaultEndDateForStartDate,
  isCalendarDateBeforeSelectedDate,
  isSameDayEndTimeSlotAllowed,
} from "@/components/storefront/date-picker/core/use-rental-date-core";

import { usePeriodLabel } from "@/hooks/use-period-label";
import { useStorefrontUrl } from "@/hooks/use-storefront-url";
import { sanitizeProductDescriptionHtml } from "@/lib/util.product-description";
import { getStorefrontPricingSummary, getStorefrontRateRows } from "@/lib/utils/storefront-pricing";

import { useAnalytics } from "@/contexts/analytics-context";
import { useCart } from "@/contexts/cart-context";
import { useStoreCurrency, useStoreMaxDiscountPercent } from "@/contexts/store-context";
import { useFormatLocale } from "@/hooks/use-format-locale";

interface PricingTier {
  id: string;
  minDuration: number | null;
  discountPercent: number | string | null;
  period?: number | null;
  price?: string | null;
  displayOrder: number | null;
}

function extractYouTubeVideoId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtu\.be\/|youtube\.com\/shorts\/)([^&?/]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

interface ProductPreviewModalProps {
  product: {
    id: string;
    name: string;
    description: string | null;
    images: string[] | null;
    price: string;
    deposit: string | null;
    quantity: number;
    category?: { name: string } | null;
    pricingMode?: PricingMode | null;
    basePeriodMinutes?: number | null;
    pricingTiers?: PricingTier[];
    videoUrl?: string | null;
    nauticalDetails?: NauticalProductDetails | null;
  };
  isOpen: boolean;
  onClose: () => void;
  storeSlug: string;
  businessHours?: BusinessHours;
  advanceNotice?: number;
  minRentalMinutes?: number;
  timezone?: string;
}

const defaultTimeSlots = generateTimeSlots("07:00", "21:00", 30);

export function ProductPreviewModal({
  product,
  isOpen,
  onClose,
  storeSlug,
  businessHours,
  advanceNotice = 0,
  minRentalMinutes = 0,
  timezone,
}: ProductPreviewModalProps) {
  const tProduct = useTranslations("storefront.product");
  const { intl: formatLocale, dateFns: dateLocale } = useFormatLocale();
  const tDateSelection = useTranslations("storefront.dateSelection");
  const currency = useStoreCurrency();
  const formatMoney = (amount: number, currencyOverride = currency) =>
    formatCurrency(amount, currencyOverride, formatLocale);
  const maxDiscountPercent = useStoreMaxDiscountPercent();
  const router = useRouter();
  const { setGlobalDates, setPricingMode } = useCart();
  const { getUrl } = useStorefrontUrl(storeSlug);
  const { trackEvent } = useAnalytics();

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Date picker state
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [startTime, setStartTime] = useState<string>("09:00");
  const [endTime, setEndTime] = useState<string>("18:00");

  const [startDateOpen, setStartDateOpen] = useState(false);
  const [startTimeOpen, setStartTimeOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [endTimeOpen, setEndTimeOpen] = useState(false);
  const [tiersExpanded, setTiersExpanded] = useState(false);

  // Reset state when modal opens and track product view
  useEffect(() => {
    if (isOpen) {
      setSelectedImageIndex(0);
      setStartDate(undefined);
      setEndDate(undefined);
      setStartTime("09:00");
      setEndTime("18:00");
      setTiersExpanded(false);
      // Track product view when modal opens
      trackEvent({
        eventType: "product_view",
        metadata: {
          productId: product.id,
          productName: product.name,
          price: product.price,
          categoryName: product.category?.name,
        },
      });
    }
  }, [isOpen, trackEvent, product.id, product.name, product.price, product.category?.name]);

  const effectivePricingMode: PricingMode = product.pricingMode ?? "day";

  useEffect(() => {
    setPricingMode(effectivePricingMode);
  }, [effectivePricingMode, setPricingMode]);

  const rateRows = useMemo(() => {
    const rows = getStorefrontRateRows(product);
    return rows.filter(
      (rate, index) =>
        rows.findIndex(
          (candidate) =>
            candidate.periodMinutes === rate.periodMinutes && candidate.price === rate.price,
        ) === index,
    );
  }, [product]);
  const pricingSummary = useMemo(() => getStorefrontPricingSummary(product), [product]);
  const displayPeriodMinutes = pricingSummary.displayPeriodMinutes;
  // Highest discount within the store limit, for the header badge
  const modalDiscount =
    maxDiscountPercent == null
      ? pricingSummary.maxReductionPercent
      : Math.max(...pricingSummary.allReductionPercents.filter((p) => p <= maxDiscountPercent), 0);
  // Whether a specific tier's discount badge should be displayed
  const isDiscountVisible = (reductionPercent: number) =>
    reductionPercent > 0 && (maxDiscountPercent == null || reductionPercent <= maxDiscountPercent);

  const images = product.images && product.images.length > 0 ? product.images : [];
  const videoId = product.videoUrl ? extractYouTubeVideoId(product.videoUrl) : null;
  const hasVideo = !!videoId;
  const totalMediaItems = images.length + (hasVideo ? 1 : 0);
  const isVideoSelected = hasVideo && selectedImageIndex === images.length;
  const nauticalStats = product.nauticalDetails
    ? [
        {
          icon: Ruler,
          value: product.nauticalDetails.lengthMeters
            ? `${product.nauticalDetails.lengthMeters} m`
            : null,
          label: "Eslora",
        },
        { icon: Users, value: product.nauticalDetails.capacity, label: "Personas" },
        { icon: BedDouble, value: product.nauticalDetails.cabins, label: "Camarotes" },
        { icon: Bath, value: product.nauticalDetails.bathrooms, label: "Baños" },
      ].filter((item) => item.value !== null && item.value !== undefined)
    : [];

  const formatPeriodLabel = usePeriodLabel();

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedImageIndex((prev) => (prev === 0 ? totalMediaItems - 1 : prev - 1));
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedImageIndex((prev) => (prev === totalMediaItems - 1 ? 0 : prev + 1));
  };

  // Date picker logic
  const minDate = useMemo(() => getMinStartDate(advanceNotice), [advanceNotice]);

  const startTimeSlots = useMemo(() => {
    if (!startDate) return defaultTimeSlots;
    const businessHoursSlots = getAvailableTimeSlots(startDate, businessHours, 30, timezone);
    // Filter out time slots that are within the advance notice period
    return businessHoursSlots.filter((slot) => isTimeSlotAvailable(startDate, slot, advanceNotice));
  }, [startDate, businessHours, advanceNotice, timezone]);

  const endTimeSlots = useMemo(() => {
    if (!endDate) return defaultTimeSlots;
    const slots = getAvailableTimeSlots(endDate, businessHours, 30, timezone);

    if (startDate && startTime && startDate.toDateString() === endDate.toDateString()) {
      return slots.filter((slot) =>
        isSameDayEndTimeSlotAllowed({
          startDate,
          startTime,
          endDate,
          endTime: slot,
          minRentalMinutes,
          timezone,
        }),
      );
    }

    return slots;
  }, [endDate, businessHours, timezone, startDate, startTime, minRentalMinutes]);

  const isDateDisabled = useCallback(
    (date: Date): boolean => {
      if (date < minDate) return true;
      if (!businessHours?.enabled) return false;
      const availability = isDateAvailable(date, businessHours, timezone);
      return !availability.available;
    },
    [businessHours, minDate, timezone],
  );

  useEffect(() => {
    if (startDate && startTimeSlots.length > 0 && !startTimeSlots.includes(startTime)) {
      setStartTime(startTimeSlots[0]);
    }
  }, [startDate, startTimeSlots, startTime]);

  useEffect(() => {
    if (endDate && endTimeSlots.length > 0 && !endTimeSlots.includes(endTime)) {
      setEndTime(endTimeSlots[endTimeSlots.length - 1] || endTimeSlots[0]);
    }
  }, [endDate, endTimeSlots, endTime]);

  const handleStartDateSelect = (date: Date | undefined) => {
    if (!date) return;
    setStartDate(date);
    setStartDateOpen(false);

    if (!endDate || date >= endDate) {
      setEndDate(
        getDefaultEndDateForStartDate({
          startDate: date,
          pricingMode: product.pricingMode || "day",
          minRentalMinutes,
          businessHours,
          timezone,
        }),
      );
    }

    setTimeout(() => setStartTimeOpen(true), 200);
  };

  const handleStartTimeSelect = (time: string) => {
    setStartTime(time);
    setStartTimeOpen(false);
    setTimeout(() => setEndDateOpen(true), 200);
  };

  const handleEndDateSelect = (date: Date | undefined) => {
    if (!date) return;
    setEndDate(date);
    setEndDateOpen(false);
    setTimeout(() => setEndTimeOpen(true), 200);
  };

  const handleEndTimeSelect = (time: string) => {
    setEndTime(time);
    setEndTimeOpen(false);
  };

  const canSubmit = startDate && endDate && startTime && endTime;

  const handleSubmit = () => {
    if (!canSubmit) return;

    const [startH, startM] = startTime.split(":").map(Number);
    const finalStart = setMinutes(setHours(startDate!, startH), startM);
    const [endH, endM] = endTime.split(":").map(Number);
    const finalEnd = setMinutes(setHours(endDate!, endH), endM);

    setGlobalDates(finalStart.toISOString(), finalEnd.toISOString());
    const params = new URLSearchParams();
    params.set("startDate", finalStart.toISOString());
    params.set("endDate", finalEnd.toISOString());

    onClose();
    router.push(`${getUrl("/rental")}?${params.toString()}`);
  };

  const TimeSelector = ({
    value,
    onSelect,
    slots,
    disabledBefore,
  }: {
    value: string;
    onSelect: (time: string) => void;
    slots: string[];
    disabledBefore?: string;
  }) => (
    <ScrollArea className="h-56">
      <div className="p-1">
        {slots.length === 0 ? (
          <div className="text-muted-foreground p-4 text-center text-sm">
            <AlertCircle className="mx-auto mb-2 h-5 w-5" />
            {tDateSelection("businessHours.storeClosed")}
          </div>
        ) : (
          slots.map((time) => {
            const isDisabled = disabledBefore ? time <= disabledBefore : false;
            const isSelected = value === time;

            return (
              <button
                key={time}
                onClick={() => !isDisabled && onSelect(time)}
                disabled={isDisabled}
                className={cn(
                  "flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors",
                  isSelected
                    ? "bg-primary text-primary-foreground"
                    : isDisabled
                      ? "text-muted-foreground/40 cursor-not-allowed"
                      : "hover:bg-muted",
                )}
              >
                <span className="font-medium">{time}</span>
                {isSelected && <Check className="h-3.5 w-3.5" />}
              </button>
            );
          })
        )}
      </div>
    </ScrollArea>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogPopup className="flex max-h-[92vh] w-[96vw] max-w-5xl flex-col gap-0 overflow-hidden p-0 md:grid md:h-[min(90vh,760px)] md:grid-cols-[minmax(0,1.3fr)_minmax(320px,.7fr)]">
        <DialogHeader className="sr-only">
          <DialogTitle>{product.name}</DialogTitle>
        </DialogHeader>

        {/* Scrollable container */}
        <div className="min-h-0 flex-1 overflow-y-auto md:overflow-hidden">
          {/* Image Section */}
          <div className="bg-muted/30 relative w-full">
            <div className="relative aspect-[16/9] w-full md:aspect-auto md:h-[min(28vh,230px)]">
              {totalMediaItems > 0 ? (
                <>
                  {isVideoSelected && videoId ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0`}
                      title={product.name}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="absolute inset-0 h-full w-full"
                    />
                  ) : images.length > 0 ? (
                    <Image
                      src={images[selectedImageIndex]}
                      alt={product.name}
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 95vw, 760px"
                      priority
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <ImageIcon className="text-muted-foreground/30 h-16 w-16" />
                    </div>
                  )}

                  {totalMediaItems > 1 && (
                    <>
                      <button
                        onClick={handlePrevImage}
                        className="bg-background/90 hover:bg-background absolute top-1/2 left-3 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full shadow-md backdrop-blur-sm transition-colors"
                        aria-label="Image précédente"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        onClick={handleNextImage}
                        className="bg-background/90 hover:bg-background absolute top-1/2 right-3 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full shadow-md backdrop-blur-sm transition-colors"
                        aria-label="Image suivante"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  )}

                  {totalMediaItems > 1 && (
                    <div className="bg-background/90 absolute right-3 bottom-3 rounded-full px-3 py-1.5 text-sm font-medium shadow-md backdrop-blur-sm">
                      {selectedImageIndex + 1} / {totalMediaItems}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex h-full items-center justify-center">
                  <ImageIcon className="text-muted-foreground/30 h-16 w-16" />
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {totalMediaItems > 1 && (
              <div className="bg-background/50 flex justify-center gap-1.5 border-b p-2">
                {images.slice(0, hasVideo ? 5 : 6).map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImageIndex(idx)}
                    className={cn(
                      "relative h-10 w-14 shrink-0 overflow-hidden rounded-md border-2 transition-all",
                      selectedImageIndex === idx
                        ? "border-primary ring-primary/20 ring-2"
                        : "border-transparent opacity-70 hover:opacity-100",
                    )}
                  >
                    <Image
                      src={img}
                      alt={`${product.name} - ${idx + 1}`}
                      fill
                      className="object-cover"
                      sizes="56px"
                    />
                  </button>
                ))}

                {images.length > (hasVideo ? 5 : 6) && (
                  <div className="bg-muted text-muted-foreground flex h-10 w-14 items-center justify-center rounded-md text-xs font-medium">
                    +{images.length - (hasVideo ? 5 : 6)}
                  </div>
                )}

                {hasVideo && videoId && (
                  <button
                    onClick={() => setSelectedImageIndex(images.length)}
                    className={cn(
                      "relative h-10 w-14 shrink-0 overflow-hidden rounded-md border-2 transition-all",
                      isVideoSelected
                        ? "border-primary ring-primary/20 ring-2"
                        : "border-transparent opacity-70 hover:opacity-100",
                    )}
                  >
                    <Image
                      src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                      alt="Vidéo"
                      fill
                      className="object-cover"
                      sizes="56px"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <Play className="h-5 w-5 fill-white text-white" />
                    </div>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Content Section */}
          <div className="p-4 md:p-5">
            {/* Header */}
            <div className="mb-3">
              <div className="mb-2 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {product.category && (
                    <p className="text-muted-foreground mb-1 text-sm">{product.category.name}</p>
                  )}
                  <h2 className="text-xl leading-tight font-semibold md:text-2xl">
                    {product.name}
                  </h2>
                </div>
                {product.quantity === 0 && (
                  <Badge variant="failed" className="shrink-0 text-xs">
                    {tProduct("unavailable")}
                  </Badge>
                )}
              </div>

              {/* Base price */}
              <div className="mt-2 flex items-baseline gap-2">
                {pricingSummary.showStartingFrom && (
                  <span className="text-muted-foreground text-sm font-medium">
                    {tProduct("startingFrom")}
                  </span>
                )}
                <span className="text-primary text-2xl font-bold">
                  {formatMoney(pricingSummary.displayPrice, currency)}
                </span>
                <span className="text-muted-foreground text-base">
                  / {formatPeriodLabel(displayPeriodMinutes)}
                </span>
                {modalDiscount > 0 && (
                  <Badge variant="progress" className="ml-2">
                    <TrendingDownSolidIcon className="mr-1 h-3 w-3" />
                    {tProduct("tieredPricing.badge", {
                      percent: Math.floor(modalDiscount),
                    })}
                  </Badge>
                )}
              </div>

              {nauticalStats.length > 0 && (
                <div className="mt-3 flex divide-x rounded-xl bg-muted/60 px-1 py-2">
                  {nauticalStats.map(({ icon: Icon, value, label }) => (
                    <div key={label} className="flex min-w-0 flex-1 items-center justify-center gap-1.5 px-2">
                      <Icon className="text-primary size-4 shrink-0" />
                      <div className="min-w-0">
                        <p className="truncate text-xs font-semibold leading-none">{value}</p>
                        <p className="text-muted-foreground mt-1 truncate text-[10px] leading-none">{label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Description */}
            {product.description && (
              <div className="relative mb-3 max-h-14 overflow-hidden border-b pb-3">
                <div
                  className="text-muted-foreground prose prose-sm dark:prose-invert wrap-break-word max-w-none text-sm leading-snug [&_h1]:text-base [&_h2]:text-base [&_h3]:text-sm [&_li]:mb-1 [&_ol]:mb-2 [&_p]:mb-2 [&_ul]:mb-2 [&>*]:break-words"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeProductDescriptionHtml(product.description),
                  }}
                />
              </div>
            )}

            {/* Pricing tiers */}
            {rateRows.length > 1 &&
              (() => {
                const MAX_VISIBLE = 3;
                const hasHidden = rateRows.length > MAX_VISIBLE;
                const hiddenCount = rateRows.length - MAX_VISIBLE;
                const visibleRows = rateRows.slice(0, MAX_VISIBLE);
                const hiddenRows = rateRows.slice(MAX_VISIBLE);

                return (
                  <div className="rounded-xl border bg-primary/5 p-3">
                    <div className="mb-2 flex items-center gap-2">
                      <div className="rounded-lg bg-primary/10 p-1.5">
                        <Layers className="h-4 w-4 text-primary" />
                      </div>
                      <span className="text-sm font-semibold">
                        {tProduct("tieredPricing.ratesTitle")}
                      </span>
                    </div>

                    <div className="space-y-1.5">
                      {visibleRows.map((rate) => (
                        <div
                          key={rate.id}
                          className="flex items-center justify-between rounded-lg bg-background px-3 py-2 text-sm shadow-xs"
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {formatPeriodLabel(rate.periodMinutes, {
                                alwaysShowCount: true,
                              })}
                            </span>
                            {isDiscountVisible(rate.reductionPercent) && (
                              <Badge variant="progress" className="text-xs font-semibold">
                                -{Math.floor(rate.reductionPercent)}%
                              </Badge>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="font-semibold">
                              {formatMoney(rate.price, currency)}
                            </span>
                            {(() => {
                              const period = minutesToPriceDuration(rate.periodMinutes);
                              if (period.duration <= 1) return null;
                              const unitMinutes = rate.periodMinutes / period.duration;
                              return (
                                <div className="text-muted-foreground text-xs">
                                  {formatMoney(rate.price / period.duration, currency)}/
                                  {formatPeriodLabel(unitMinutes)}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      ))}

                      {/* Collapsible hidden tiers */}
                      {hasHidden && (
                        <div
                          className="grid transition-[grid-template-rows] duration-300 ease-in-out"
                          style={{
                            gridTemplateRows: tiersExpanded ? "1fr" : "0fr",
                          }}
                        >
                          <div className="overflow-hidden">
                            <div className="space-y-1.5">
                              {hiddenRows.map((rate) => (
                                <div
                                  key={rate.id}
                                  className="flex items-center justify-between rounded-lg bg-background px-3 py-2 text-sm shadow-xs"
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">
                                      {formatPeriodLabel(rate.periodMinutes, {
                                        alwaysShowCount: true,
                                      })}
                                    </span>
                                    {isDiscountVisible(rate.reductionPercent) && (
                                      <Badge variant="progress" className="text-xs font-semibold">
                                        -{Math.floor(rate.reductionPercent)}%
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <span className="font-semibold">
                                      {formatMoney(rate.price, currency)}
                                    </span>
                                    {(() => {
                                      const period = minutesToPriceDuration(rate.periodMinutes);
                                      if (period.duration <= 1) return null;
                                      const unitMinutes = rate.periodMinutes / period.duration;
                                      return (
                                        <div className="text-muted-foreground text-xs">
                                          {formatMoney(rate.price / period.duration, currency)}/
                                          {formatPeriodLabel(unitMinutes)}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {hasHidden && (
                        <button
                          type="button"
                          onClick={() => setTiersExpanded((v) => !v)}
                          className="text-muted-foreground hover:text-foreground flex w-full items-center justify-center gap-1 pt-1 text-xs font-medium transition-colors"
                        >
                          {tiersExpanded
                            ? tProduct("tieredPricing.showLess")
                            : tProduct("tieredPricing.showMore", {
                                count: hiddenCount,
                              })}
                          {tiersExpanded ? (
                            <ChevronUp className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}
          </div>
        </div>

        {/* Footer with Date Picker */}
        <div className="bg-muted/30 shrink-0 border-t p-4 md:flex md:h-full md:items-center md:border-t-0 md:border-l md:p-5">
          <div className="flex w-full flex-col gap-3">
            <div className="mb-2">
              <p className="text-primary text-xs font-semibold tracking-[0.16em] uppercase">
                {tProduct("selectDates")}
              </p>
              <p className="mt-1 text-xl font-semibold">{product.name}</p>
              <p className="text-muted-foreground mt-1 text-sm">
                {formatMoney(pricingSummary.displayPrice, currency)} / {formatPeriodLabel(displayPeriodMinutes)}
              </p>
            </div>

            {/* Date/Time inputs */}
            <div className="grid grid-cols-1 gap-3">
              {/* Start Date/Time */}
              <div>
                <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
                  {tDateSelection("startLabel")}
                </label>
                <div className="bg-background flex h-11 overflow-hidden rounded-xl border">
                  <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                    <PopoverTrigger
                      render={
                        <button
                          className={cn(
                            "hover:bg-muted/50 flex min-w-0 flex-1 items-center gap-2 px-3 text-left transition-colors",
                            !startDate && "text-muted-foreground",
                          )}
                        />
                      }
                    >
                      <CalendarIcon className="text-primary h-4 w-4 shrink-0" />
                      <span className="truncate text-sm font-medium">
                        {startDate
                          ? format(startDate, "d MMM", { locale: dateLocale })
                          : tDateSelection("startDate")}
                      </span>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={handleStartDateSelect}
                        disabled={isDateDisabled}
                        locale={dateLocale}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  <div className="bg-border my-2 w-px" />

                  <Popover open={startTimeOpen} onOpenChange={setStartTimeOpen}>
                    <PopoverTrigger
                      render={
                        <button className="hover:bg-muted/50 flex shrink-0 items-center gap-1.5 px-3 transition-colors" />
                      }
                    >
                      <Clock className="text-muted-foreground h-4 w-4" />
                      <span className="text-sm font-medium">{startTime}</span>
                    </PopoverTrigger>
                    <PopoverContent className="w-40 p-0" align="start">
                      <TimeSelector
                        value={startTime}
                        onSelect={handleStartTimeSelect}
                        slots={startTimeSlots}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* End Date/Time */}
              <div>
                <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
                  {tDateSelection("endLabel")}
                </label>
                <div className="bg-background flex h-11 overflow-hidden rounded-xl border">
                  <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                    <PopoverTrigger
                      render={
                        <button
                          className={cn(
                            "hover:bg-muted/50 flex min-w-0 flex-1 items-center gap-2 px-3 text-left transition-colors",
                            !endDate && "text-muted-foreground",
                          )}
                        />
                      }
                    >
                      <CalendarIcon className="text-primary h-4 w-4 shrink-0" />
                      <span className="truncate text-sm font-medium">
                        {endDate
                          ? format(endDate, "d MMM", { locale: dateLocale })
                          : tDateSelection("endDate")}
                      </span>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={handleEndDateSelect}
                        disabled={(date) =>
                          isDateDisabled(date) || isCalendarDateBeforeSelectedDate(date, startDate)
                        }
                        locale={dateLocale}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  <div className="bg-border my-2 w-px" />

                  <Popover open={endTimeOpen} onOpenChange={setEndTimeOpen}>
                    <PopoverTrigger
                      render={
                        <button className="hover:bg-muted/50 flex shrink-0 items-center gap-1.5 px-3 transition-colors" />
                      }
                    >
                      <Clock className="text-muted-foreground h-4 w-4" />
                      <span className="text-sm font-medium">{endTime}</span>
                    </PopoverTrigger>
                    <PopoverContent className="w-40 p-0" align="end">
                      <TimeSelector
                        value={endTime}
                        onSelect={handleEndTimeSelect}
                        slots={endTimeSlots}
                        disabledBefore={
                          startDate &&
                          endDate &&
                          startDate.toDateString() === endDate.toDateString()
                            ? startTime
                            : undefined
                        }
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || product.quantity === 0}
              size="lg"
              className="h-12 w-full text-base font-semibold"
            >
              {tDateSelection("viewAvailability")}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>

            {/* Full product page: the indexable, shareable version of this preview. */}
            <Button
              variant="ghost"
              className="mt-2 w-full"
              render={<Link href={getUrl(`/product/${product.id}`)} />}
            >
              {tProduct("viewDetails")}
            </Button>
          </div>
        </div>
      </DialogPopup>
    </Dialog>
  );
}
